import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import axios from "axios";
import {
  Waves, ArrowRight, ArrowLeft, Kanban, GitBranch, Target,
  BarChart3, Mail, Lock, Loader2, AlertCircle, Send, Sparkles, Brain
} from "lucide-react";
import TrialModal from "./TrialModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { user, setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [showTrial, setShowTrial] = useState(false);
  const [deployDate, setDeployDate] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/version`).then(r => {
      const d = new Date(r.data.deployed_at);
      const dd = String(d.getDate()).padStart(2,"0");
      const mm = String(d.getMonth()+1).padStart(2,"0");
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2,"0");
      const mi = String(d.getMinutes()).padStart(2,"0");
      setDeployDate(`${dd}${mm}${yyyy}${hh}${mi}`);
    }).catch(() => {});
  }, []);

  // 2-step state
  const [step, setStep] = useState(1); // 1=email, 2=password, 3=set-password-sent
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  // Step 1: Check email
  const handleCheckEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/check-email`, { email: email.trim().toLowerCase() });
      setUserName(res.data.name || "");
      if (res.data.has_password) {
        setStep(2);
      } else {
        // User exists but no password — send set-password email
        await axios.post(`${API}/auth/send-set-password`, { email: email.trim().toLowerCase() });
        setStep(3);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "E-mail nao encontrado.");
    }
    setLoading(false);
  };

  // Step 2: Login with password
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      if (res.data.session_token) {
        localStorage.setItem("session_token", res.data.session_token);
      }
      await checkAuth();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      console.error("[LOGIN ERROR]", { status, detail, data: err.response?.data, message: err.message });
      if (status === 401) {
        setError(detail || "E-mail ou senha incorretos.");
      } else if (status === 429) {
        setError(detail || "Muitas tentativas. Aguarde alguns minutos.");
      } else {
        setError(detail || `Erro no servidor (${status || "rede"}). Tente novamente.`);
      }
    }
    setLoading(false);
  };

  // Forgot password
  const handleForgotPassword = async () => {
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setEmailSent(true);
    } catch {
      setError("Erro ao enviar e-mail. Tente novamente.");
    }
    setLoading(false);
  };

  // Resend set-password email
  const handleResendSetPassword = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-set-password`, { email: email.trim().toLowerCase() });
      setEmailSent(true);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const features = [
    { icon: Kanban, title: "Kanban", desc: "Visualize e mova negócios por etapa" },
    { icon: GitBranch, title: "Pipelines", desc: "Múltiplos funis configuráveis" },
    { icon: Brain, title: "IA Integrada", desc: "Resumos e próximos passos com IA" },
    { icon: BarChart3, title: "Dashboard", desc: "KPIs e performance em tempo real" },
  ];

  return (
    <div data-testid="login-page" className="min-h-screen flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: "#3B82F6" }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-300 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="flex items-center gap-3 mb-12">
            <img src="/brand/icon-2.png" alt="Pipe4.me" style={{ height: "6.5rem" }} className="object-contain" />
          </div>
          <p className="text-lg text-blue-100 leading-relaxed mb-12 max-w-md">
            CRM inteligente com IA que analisa seus negócios, sugere próximos passos e automatiza sua rotina comercial.
          </p>
          <div className="grid grid-cols-2 gap-4">
            {features.map((f, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <f.icon className="w-5 h-5 mb-2 text-blue-200" />
                <h3 className="font-semibold text-sm mb-1">{f.title}</h3>
                <p className="text-xs text-blue-200">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 bg-slate-50">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center mb-10">
            <img src="/brand/logo-3.png" alt="Pipe4.me" className="h-10 object-contain" />
          </div>

          {/* Step 1: Email */}
          {step === 1 && (
            <>
              <h2 data-testid="login-title" className="text-3xl font-bold text-slate-900 mb-2">Entrar</h2>
              <p className="text-slate-500 mb-8">Digite seu e-mail para continuar.</p>

              <form onSubmit={handleCheckEmail} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">E-mail</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      data-testid="login-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="seu@email.com"
                      autoFocus
                      className="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent"
                    />
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <Button
                  data-testid="login-continue-btn"
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="w-full h-12 text-white rounded-xl text-sm font-medium shadow-sm flex items-center justify-center gap-2 transition-colors hover:opacity-90"
                  style={{ backgroundColor: "#3B82F6" }}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continuar <ArrowRight className="w-4 h-4" /></>}
                </Button>
              </form>
            </>
          )}

          {/* Step 2: Password */}
          {step === 2 && (
            <>
              <button
                data-testid="login-back-btn"
                onClick={() => { setStep(1); setError(""); setPassword(""); setEmailSent(false); }}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Voltar
              </button>

              <h2 data-testid="login-password-title" className="text-3xl font-bold text-slate-900 mb-2">
                {userName ? `Olá, ${userName}!` : "Digite sua senha"}
              </h2>
              <p className="text-slate-500 mb-1 text-sm">{email}</p>
              <p className="text-slate-400 mb-6 text-xs">Digite sua senha para acessar.</p>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Senha</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      data-testid="login-password-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Sua senha"
                      autoFocus
                      className="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent"
                    />
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    {error}
                  </div>
                )}

                {emailSent && (
                  <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    <Send className="w-3.5 h-3.5 flex-shrink-0" />
                    E-mail de recuperação enviado! Verifique sua caixa de entrada.
                  </div>
                )}

                <Button
                  data-testid="login-submit-btn"
                  type="submit"
                  disabled={loading || !password}
                  className="w-full h-12 text-white rounded-xl text-sm font-medium shadow-sm flex items-center justify-center gap-2 transition-colors hover:opacity-90"
                  style={{ backgroundColor: "#3B82F6" }}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Entrar <ArrowRight className="w-4 h-4" /></>}
                </Button>

                <button
                  data-testid="forgot-password-btn"
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={loading}
                  className="w-full text-center text-sm text-[#3B82F6] hover:underline"
                >
                  Esqueci minha senha
                </button>
              </form>
            </>
          )}

          {/* Step 3: Set password email sent (first-time user) */}
          {step === 3 && (
            <>
              <button
                onClick={() => { setStep(1); setError(""); setEmailSent(false); }}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Voltar
              </button>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-[#3B82F6]" />
                </div>
                <h2 data-testid="set-password-sent-title" className="text-2xl font-bold text-slate-900 mb-2">Defina sua senha</h2>
                <p className="text-slate-500 text-sm mb-2">
                  Enviamos um e-mail para <strong className="text-slate-700">{email}</strong> com um link para definir sua senha.
                </p>
                <p className="text-slate-400 text-xs mb-6">Verifique também a pasta de spam.</p>

                {emailSent && (
                  <div className="flex items-center justify-center gap-2 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-4">
                    <Send className="w-3.5 h-3.5 flex-shrink-0" />
                    E-mail reenviado com sucesso!
                  </div>
                )}

                <Button
                  data-testid="resend-set-password-btn"
                  onClick={handleResendSetPassword}
                  disabled={loading}
                  variant="outline"
                  className="w-full h-11 rounded-xl text-sm font-medium border-2 border-slate-200 text-slate-600 hover:bg-slate-50 gap-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Reenviar e-mail <Send className="w-4 h-4" /></>}
                </Button>
              </div>
            </>
          )}

          {/* Trial CTA - always visible */}
          {step !== 3 && (
            <>
              <p className="mt-6 text-center text-xs text-slate-400">
                Ao entrar, você concorda com nossos{" "}
                <a href="/termos-de-uso" target="_blank" className="text-[#3B82F6] hover:underline">Termos de Uso</a>.
              </p>

              <div className="mt-8">
                <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-5 text-center">
                  <p className="text-sm font-medium text-slate-700 mb-1">Ainda não tem conta?</p>
                  <p className="text-xs text-slate-400 mb-4">Crie seu workspace em menos de 1 minuto.<br />Sem cartão de crédito.</p>
                  <Button
                    data-testid="trial-cta-btn"
                    onClick={() => setShowTrial(true)}
                    variant="outline"
                    className="w-full h-11 rounded-xl text-sm font-semibold border-2 border-blue-500 text-blue-600 hover:bg-blue-500 hover:text-white transition-all gap-2"
                  >
                    Testar grátis por 14 dias
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {deployDate && (
                <p className="mt-4 text-center text-[11px] text-slate-300" data-testid="deploy-date">
                  Hash: {deployDate}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {showTrial && <TrialModal onClose={() => setShowTrial(false)} />}
    </div>
  );
}
